<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { iButton, iButtonSizes, iFeaturedIconTypes } from '@inscribe/signal';
import DecisionEngineCard from '@/V2/decision-engine/DecisionEngineCard.vue';
import EmptyPage from '@/V2/components/EmptyPage';
import BlocklistDelete from '@/V2/decision-engine/blocklist/BlocklistDelete.vue';
import BlocklistUpload from '@/V2/decision-engine/blocklist/BlocklistUpload.vue';
import DecisionEngineItemDescription from '@/V2/decision-engine/DecisionEngineItemDescription.vue';
import PageHeader from '@/V2/components/PageHeader.vue';

export default {
  components: {
    BlocklistDelete,
    BlocklistUpload,
    DecisionEngineCard,
    EmptyPage,
    iButton,
    DecisionEngineItemDescription,
    PageHeader,
  },
  data() {
    return {
      isLoading: true,
      iFeaturedIconTypes,
      blockToBeDeleted: null,
      uploadModalVisibile: false,
      iButtonSizes,
    };
  },
  computed: {
    ...mapGetters(['isOrgAdmin', 'getBlocklist']),
    ...mapActions(['loadBlocklist']),
  },
  mounted() {
    this.loadBlocklist.then(() => {
      this.isLoading = false;
    });
  },
  methods: {
    ...mapMutations(['setCurrentEditingItem']),
    onDeleteClick(params) {
      this.$emit('hide-tooltips');
      this.blockToBeDeleted = params.item;
    },
    onEditClick(params) {
      this.$emit('hide-tooltips');
      this.setCurrentEditingItem(params.item);
      this.$router.push({
        name: 'decision-engine-blocklist-edit',
      });
    },
    uploadDatabase() {
      this.uploadModalVisibile = true;
    },
    createBlocklist() {
      const newBlocklist = {
        address: {
          raw: '',
          normalised: '',
        },
        id: '',
        inEdit: true,
        name: '',
      };
      this.setCurrentEditingItem(newBlocklist);
      this.$router.push({
        name: 'decision-engine-blocklist-edit',
      });
    },
    getBlockTitle(entry) {
      return entry.name;
    },
    getBlockSubtitle(entry) {
      return entry.address?.normalized || entry.address?.raw || '';
    },
  },
};
</script>

<template>
  <div class="blocklist d-flex flex-column">
    <PageHeader title="Blocklist">
      <template #actions>
        <i-button
          v-if="isOrgAdmin"
          text="Upload Database"
          type="neutral"
          subtype="outline"
          :size="iButtonSizes.MEDIUM"
          @button-click="uploadDatabase()"
        ></i-button>
      </template>
      <template #primary-actions>
        <i-button
          v-if="isOrgAdmin"
          text="New Blocklist"
          data-testid="new-blocklist-button"
          :size="iButtonSizes.MEDIUM"
          @button-click="createBlocklist()"
        ></i-button>
      </template>
    </PageHeader>

    <!-- page content -->
    <div class="decision-engine-page-content d-flex flex-column i-gap-12">
      <!-- item description -->
      <decision-engine-item-description
        title="Blocklist"
        description="Catch repeat offenders by adding their details to a blocklist."
      />

      <!-- empty card -->
      <empty-page
        v-if="getBlocklist.length === 0 && !isLoading"
        class="d-flex flex-grow-1"
        icon="large-ai-spark-document"
        title="Start by adding a customer to the blocklist"
        subtitle="All of your blocklist details will appear here."
        button-text="New Blocklist"
        secondary-button-text="Upload Database"
        @on-button-click="createBlocklist()"
        @on-secondary-button-click="uploadDatabase()"
      ></empty-page>

      <div
        v-if="getBlocklist.length > 0 && !isLoading"
        class="blocks-list rules-list items-list i-radius-4xl"
        data-testid="blocklist-rules-list"
      >
        <decision-engine-card
          v-for="(entry, index) in getBlocklist"
          :key="index"
          :title="getBlockTitle(entry)"
          :subtitle="getBlockSubtitle(entry)"
          :item="entry"
          icon="medium-user-delete"
          :icon-type="iFeaturedIconTypes.ERROR"
          tooltip-toggle-on="Disable Details"
          tooltip-toggle-off="Enable Details"
          tooltip-edit="Edit Details"
          tooltip-delete="Delete Details"
          @delete-click="onDeleteClick"
          @edit-click="onEditClick"
          @card-click="onEditClick"
        ></decision-engine-card>
      </div>
    </div>

    <!-- delete -->
    <blocklist-delete
      :block-to-be-deleted="blockToBeDeleted"
      @on-close="blockToBeDeleted = null"
    ></blocklist-delete>

    <!-- upload database -->
    <blocklist-upload
      :visible="uploadModalVisibile"
      @on-close="uploadModalVisibile = false"
    ></blocklist-upload>
  </div>
</template>

<style lang="scss" scoped>
.blocklist {
  height: 100%;

  .content {
    height: 100%;
  }
}
</style>
